"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
import styles from "./price-action.module.css";

type PatternRow = {
  symbol: string;
  close?: number | null;
  pattern: string;
  pattern_family?: string;
  direction: string;
  status: string;
  signal_stage?: string;
  boundary?: number | null;
  breakout_price?: number | null;
  breakout_direction?: string | null;
  volume_ratio?: number | null;
  target_price?: number | null;
  pattern_duration?: number | null;
  date?: string;
  image_url?: string | null;
  confidence?: number | null;
  strength_score?: number | null;
  breakout_score?: number | null;
  /** @deprecated same value as breakout_score; not a probability. */
  breakout_probability?: number | null;
  trend_confirmed?: boolean | null;
  volume_confirmed?: boolean | null;
  momentum_confirmed?: boolean | null;
  recommendation?: string | null;
  distance_to_boundary_pct?: number | null;
  quality_score?: number | null;
  quality_lane?: string | null;
  alternate_patterns?: string[] | null;
  alternate_count?: number | null;
};

type LaneBucket = {
  label: string;
  description: string;
  count: number;
  results: PatternRow[];
};

type PriceActionPayload = {
  ok: boolean;
  generated_at?: string | null;
  scan_date?: string | null;
  summary?: {
    selected_count?: number;
    total_results?: number;
    strict_count?: number;
    exploratory_count?: number;
    image_backed_count?: number;
    chartless_count?: number;
    stage_counts?: {
      breakout?: number;
      breakdown?: number;
      retest?: number;
    };
  };
  lanes?: {
    strict?: LaneBucket;
    exploratory?: LaneBucket;
  };
};

const ACTIVE_LANES = ["strict", "exploratory"] as const;
type ActiveLane = (typeof ACTIVE_LANES)[number];
const EMPTY_ROWS: PatternRow[] = [];
const PAGE_SIZE = 25;

function formatNumber(value: number | null | undefined, digits = 2) {
  if (value == null || Number.isNaN(value)) return "—";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatPercent(value: number | null | undefined, digits = 1) {
  if (value == null || Number.isNaN(value)) return "—";
  return `${value.toFixed(digits)}%`;
}

function formatDate(value: string | null | undefined) {
  if (!value) return "Latest scan";
  // Strip any time component before parsing as a date-only value
  const datePart = value.split("T")[0];
  const parsed = new Date(`${datePart}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(parsed);
}

function signalToneClass(direction: string) {
  if (direction === "Bullish") return styles.positiveTone;
  if (direction === "Bearish") return styles.negativeTone;
  return styles.neutralTone;
}

function confirmationToneClass(value: boolean | null | undefined) {
  if (value === true) return styles.confirmPositive;
  if (value === false) return styles.confirmNegative;
  return styles.confirmNeutral;
}

function confirmationLabel(name: string, value: boolean | null | undefined) {
  if (value === true) return `${name} ✓`;
  if (value === false) return `${name} ✗`;
  return name;
}

export default function PriceActionScannerPage() {
  const [payload, setPayload] = useState<PriceActionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeLane, setActiveLane] = useState<ActiveLane>("strict");
  const [patternFilter, setPatternFilter] = useState("ALL");
  const [directionFilter, setDirectionFilter] = useState("ALL");
  const [stageFilter, setStageFilter] = useState("ALL");
  const [chartBackedOnly, setChartBackedOnly] = useState(true);
  const [expandedImage, setExpandedImage] = useState<{ url: string; alt: string } | null>(null);
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({
          signals_only: "true",
          exclude_forming: "true",
          exclude_failed: "true",
          include_retest: "true",
        });
        const res = await fetch(`/api/price-action?${params.toString()}`);
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = (await res.json()) as PriceActionPayload;
        setPayload(data);

        const strictCount = data.lanes?.strict?.count ?? 0;
        const exploratoryCount = data.lanes?.exploratory?.count ?? 0;
        if (strictCount === 0 && exploratoryCount > 0) {
          setActiveLane("exploratory");
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    if (!expandedImage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpandedImage(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [expandedImage]);

  // Reset filters synchronously when switching lanes to avoid a stale-filter empty flash
  const handleLaneSwitch = useCallback((lane: ActiveLane) => {
    setActiveLane(lane);
    setPatternFilter("ALL");
    setStageFilter("ALL");
    setDisplayLimit(PAGE_SIZE);
  }, []);

  const activeLaneBucket = useMemo(() => {
    if (!payload?.lanes) return null;
    return payload.lanes[activeLane] ?? null;
  }, [activeLane, payload]);

  const laneRows = useMemo(() => activeLaneBucket?.results ?? EMPTY_ROWS, [activeLaneBucket]);

  const patterns = useMemo(
    () => Array.from(new Set(laneRows.map((row) => row.pattern).filter(Boolean))).sort(),
    [laneRows],
  );

  const stages = useMemo(
    () =>
      Array.from(
        new Set(laneRows.map((row) => row.signal_stage).filter(Boolean) as string[]),
      ).sort(),
    [laneRows],
  );

  // Reset display limit when any filter changes
  useEffect(() => {
    setDisplayLimit(PAGE_SIZE);
  }, [patternFilter, directionFilter, stageFilter, chartBackedOnly]);

  const filteredRows = useMemo(() => {
    return laneRows.filter((row) => {
      if (chartBackedOnly && !row.image_url) return false;
      if (patternFilter !== "ALL" && row.pattern !== patternFilter) return false;
      if (directionFilter !== "ALL" && row.direction !== directionFilter) return false;
      if (stageFilter !== "ALL" && row.signal_stage !== stageFilter) return false;
      return true;
    });
  }, [chartBackedOnly, directionFilter, laneRows, patternFilter, stageFilter]);

  const paginatedRows = useMemo(() => filteredRows.slice(0, displayLimit), [displayLimit, filteredRows]);

  const summaryCards = [
    {
      label: "Priority Setups",
      value: payload?.summary?.strict_count ?? 0,
      note: "Highest-ranked by confidence, breakout score, and boundary freshness.",
    },
    {
      label: "Exploratory Patterns",
      value: payload?.summary?.exploratory_count ?? 0,
      note: "Monitoring lane for weaker or less-confirmed structures.",
    },
    {
      label: "Charts Available",
      value: payload?.summary?.image_backed_count ?? 0,
      note: "Chart-backed filter is on by default. Toggle it off to see all rows.",
    },
    {
      label: "Latest Session",
      value: formatDate(payload?.scan_date),
      note: "Signals are based on the most recent completed daily session.",
    },
  ];

  return (
    <main>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Price Action Command Board</h1>
          <p className="page-subtitle">
            Ranked breakout, breakdown, and retest structures with explicit quality
            lanes instead of a silent weak-volume fallback.
          </p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <div className={styles.heroPanel}>
            <div className={styles.heroCopy}>
              <div className={styles.eyebrow}>Signal Quality</div>
              <h2>Separate trade-ready structures from monitoring noise</h2>
              <p>
                Priority setups are ranked using backend confidence, breakout score,
                confirmation signals, and distance from the breakout boundary. Channels are
                still visible, but they must score higher to earn the top lane.
              </p>
            </div>
            <div className={styles.heroNotes}>
              <div className={styles.heroNote}>
                <strong>Priority setups</strong>
                <span>Higher-quality signals worth immediate review.</span>
              </div>
              <div className={styles.heroNote}>
                <strong>Exploratory patterns</strong>
                <span>Lower-conviction structures to monitor, not blindly trade.</span>
              </div>
            </div>
          </div>

          <div className={styles.summaryGrid}>
            {summaryCards.map((card) => (
              <article key={card.label} className={styles.summaryCard}>
                <span className={styles.summaryLabel}>{card.label}</span>
                <strong className={styles.summaryValue}>{card.value}</strong>
                <p>{card.note}</p>
              </article>
            ))}
          </div>

          <div className={styles.laneSwitch}>
            {ACTIVE_LANES.map((laneKey) => {
              const lane = payload?.lanes?.[laneKey];
              const active = activeLane === laneKey;
              return (
                <button
                  key={laneKey}
                  type="button"
                  className={`ui-button ${active ? "ui-button-primary" : "ui-button-ghost"} ${styles.laneButton}`}
                  onClick={() => handleLaneSwitch(laneKey)}
                >
                  <span className={styles.laneButtonCopy}>
                    <strong>{lane?.label ?? laneKey}</strong>
                    <small>{lane?.description}</small>
                  </span>
                  <span className={styles.laneCount}>{lane?.count ?? 0}</span>
                </button>
              );
            })}
          </div>

          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <label htmlFor="price-action-pattern">Pattern</label>
              <select
                id="price-action-pattern"
                className={styles.select}
                value={patternFilter}
                onChange={(e) => setPatternFilter(e.target.value)}
              >
                <option value="ALL">All patterns</option>
                {patterns.map((pattern) => (
                  <option key={pattern} value={pattern}>
                    {pattern}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label htmlFor="price-action-direction">Direction</label>
              <select
                id="price-action-direction"
                className={styles.select}
                value={directionFilter}
                onChange={(e) => setDirectionFilter(e.target.value)}
              >
                <option value="ALL">All directions</option>
                <option value="Bullish">Bullish</option>
                <option value="Bearish">Bearish</option>
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label htmlFor="price-action-stage">Signal stage</label>
              <select
                id="price-action-stage"
                className={styles.select}
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
              >
                <option value="ALL">All stages</option>
                {stages.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className={`ui-button ${chartBackedOnly ? "ui-button-primary" : "ui-button-ghost"} ${styles.chartToggle}`}
              onClick={() => setChartBackedOnly((prev) => !prev)}
            >
              {chartBackedOnly ? "Chart-backed only" : "Include chartless rows"}
            </button>

            <div className={styles.filterMeta}>
              Showing <strong>{paginatedRows.length}</strong> of <strong>{filteredRows.length}</strong>
            </div>
          </div>

          {loading && <div className={styles.emptyState}>Loading price action board...</div>}
          {error && <div className={styles.errorState}>{error}</div>}

          {!loading && !error && activeLaneBucket && (
            <>
              <div className={styles.activeLaneNote}>
                <strong>{activeLaneBucket.label}:</strong> {activeLaneBucket.description}
              </div>

              <div className={styles.tableWrap}>
                {filteredRows.length === 0 ? (
                  <div className={styles.emptyState}>
                    No rows match the current lane and filters.
                  </div>
                ) : (
                  <>
                    <table className={`results-table ${styles.resultsTable}`}>
                      <thead>
                        <tr>
                          <th>Chart</th>
                          <th>Setup</th>
                          <th>Signal</th>
                          <th>Quality</th>
                          <th>Confirmations</th>
                          <th>Levels</th>
                          <th>Volume</th>
                          <th>Recommendation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.map((row) => (
                          <tr key={`${row.symbol}-${row.pattern}-${row.status}-${row.boundary ?? ""}`}>
                            <td>
                              {row.image_url ? (
                                <Image
                                  src={row.image_url}
                                  alt={`${row.symbol} price action chart`}
                                  width={132}
                                  height={74}
                                  unoptimized
                                  className={styles.chartImage}
                                  onClick={() =>
                                    setExpandedImage({
                                      url: row.image_url as string,
                                      alt: `${row.symbol} price action chart`,
                                    })
                                  }
                                  role="button"
                                  aria-label={`Expand chart for ${row.symbol}`}
                                  tabIndex={0}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      setExpandedImage({
                                        url: row.image_url as string,
                                        alt: `${row.symbol} price action chart`,
                                      });
                                    }
                                  }}
                                />
                              ) : (
                                <div className={styles.chartFallback}>
                                  <span>No chart</span>
                                  <strong>{formatNumber(row.quality_score, 1)}</strong>
                                  <small>{row.signal_stage ?? "Signal"}</small>
                                </div>
                              )}
                            </td>

                            <td>
                              <div className={styles.setupCell}>
                                <strong>{row.symbol}</strong>
                                <span>{row.pattern}</span>
                                <small>{row.pattern_family ?? "Pattern"}</small>
                                {row.alternate_count ? (
                                  <small
                                    className={styles.alternatePatterns}
                                    title={(row.alternate_patterns ?? []).join(", ")}
                                  >
                                    +{row.alternate_count} more {row.alternate_count === 1 ? "pattern" : "patterns"}
                                  </small>
                                ) : null}
                              </div>
                            </td>

                            <td>
                              <div className={styles.signalCell}>
                                <div className={styles.signalChips}>
                                  <span className={`${styles.signalChip} ${signalToneClass(row.direction)}`}>
                                    {row.direction}
                                  </span>
                                  <span className={`${styles.signalChip} ${styles.stageChip}`}>
                                    {row.signal_stage ?? "Signal"}
                                  </span>
                                </div>
                                <span className={styles.statusCopy}>{row.status}</span>
                              </div>
                            </td>

                            <td>
                              <div className={styles.qualityCell}>
                                <strong>{formatNumber(row.quality_score, 1)}</strong>
                                <span>Confidence {formatPercent(row.confidence)}</span>
                                <span title="Uncalibrated 0-100 confluence score — not a probability">Breakout {row.breakout_score ?? row.breakout_probability ?? "—"}/100</span>
                              </div>
                            </td>

                            <td>
                              <div className={styles.confirmationCell}>
                                <span className={`${styles.confirmChip} ${confirmationToneClass(row.trend_confirmed)}`}>
                                  {confirmationLabel("Trend", row.trend_confirmed)}
                                </span>
                                <span className={`${styles.confirmChip} ${confirmationToneClass(row.volume_confirmed)}`}>
                                  {confirmationLabel("Volume", row.volume_confirmed)}
                                </span>
                                <span className={`${styles.confirmChip} ${confirmationToneClass(row.momentum_confirmed)}`}>
                                  {confirmationLabel("Momentum", row.momentum_confirmed)}
                                </span>
                              </div>
                            </td>

                            <td>
                              <div className={styles.levelsCell}>
                                <span>Close {formatNumber(row.close)}</span>
                                <span>Boundary {formatNumber(row.boundary)}</span>
                                <span>Target {formatNumber(row.target_price)}</span>
                                <small>
                                  {row.distance_to_boundary_pct != null
                                    ? `${formatPercent(row.distance_to_boundary_pct)} from boundary`
                                    : "Boundary distance unavailable"}
                                </small>
                              </div>
                            </td>

                            <td>
                              <div className={styles.volumeCell}>
                                <strong>{row.volume_ratio ? `${row.volume_ratio.toFixed(2)}x` : "—"}</strong>
                                <span>{row.volume_confirmed ? "Confirmed volume" : "No full confirmation"}</span>
                                <small>
                                  {row.pattern_duration ? `${row.pattern_duration} day structure` : "Duration unavailable"}
                                </small>
                              </div>
                            </td>

                            <td>
                              <div className={styles.recommendationCell}>
                                <strong>{row.recommendation ?? "Monitor structure"}</strong>
                                <span>{formatDate(row.date)}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {paginatedRows.length < filteredRows.length && (
                      <div className={styles.loadMoreWrap}>
                        <button
                          type="button"
                          className="ui-button ui-button-primary"
                          onClick={() => setDisplayLimit((prev) => prev + PAGE_SIZE)}
                        >
                          Load More ({filteredRows.length - paginatedRows.length} remaining)
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {expandedImage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={expandedImage.alt}
          className={styles.modalBackdrop}
          onClick={() => setExpandedImage(null)}
        >
          <div className={styles.modalPanel} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              aria-label="Close chart"
              className={styles.modalClose}
              onClick={() => setExpandedImage(null)}
            >
              ✕
            </button>
            <Image
              src={expandedImage.url}
              alt={expandedImage.alt}
              width={1200}
              height={700}
              unoptimized
              sizes="90vw"
              className={styles.modalImage}
            />
          </div>
        </div>
      )}
    </main>
  );
}
